import { Badge, Box, Button, Drawer, FormControl, IconButton, InputAdornment, TextField, Theme, Typography, useTheme } from "@mui/material";
import { CommonLayout } from "../commonComponents";
import { SearchIcon } from "../customSVGs";
import { useEffect, useState } from "react";
import { DialogCloseIcon, FilterIcon } from "../../assets/SvgIcons";
import CustomTable from "../investorOverview/common/customTable";
import { Order, PaginationObj, SortingObj, TableHeaderData } from "../investorOverview/types";
import OrdersApprovalTableBody from "./ordersApprovalTableBody";
import CustomFilter from "../investorOverview/common/CustomFilter";
import { OrderHistoryRes, OrderListObj, ProductTypeFilter } from "../../redux-store/types/orderManagement";
import { getApprovalTypeFilter, getProductTypeFilter } from "../../redux-store/actions/orderManagement";
import { useDispatch } from "react-redux";
import { useDebounce } from "../../utils/useDebounce";
import { getOrderApprovalList } from "../../redux-store/actions/orderApproval";
import { ApprovalStatuFilter } from "../../redux-store/types/orderApproval";
import { whiteSpaceOnlyRegex } from "../../utils/regex";


const tableHeaderData: TableHeaderData[] = [
    {
        id: 'orderNumber',
        label: 'ORDER NUMBER',
        align: 'center',
        isSortable: true
    },
    {
        id: 'orderDate',
        label: 'ORDER DATE',
        align: 'center',
        isSortable: true
    },
    {
        id: 'clientName',
        label: 'INVESTOR NAME',
        align: 'center',
        isSortable: true
    },
    {
        id: 'productType',
        label: 'PRODUCT',
        align: 'center',
        isSortable: true
    },
    {
        id: 'portfolioName',
        label: 'ACCOUNT',
        align: 'center',
        isSortable: true
    },
    {
        id: 'accountType',
        label: 'ACCOUNT TYPE',
        align: 'center',
        isSortable: true
    },
    {
        id: 'paymentStatus',
        label: 'PAYMENT STATUS',
        align: 'center',
        isSortable: true
    },
    {
        id: 'approvalStatus',
        label: 'ORDER STATUS',
        align: 'center',
        isSortable: true
    },
]



export default function OrdersApproval(): JSX.Element {
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const [productFilterData, setProductFilterData] = useState<ProductTypeFilter[]>([])
    const [approvalFilterData, setApprovalFilterData] = useState<ApprovalStatuFilter[]>([])
    const [searchText, setSearchText] = useState<string>('');
    const [orderApprovalLoading, setOrderApprovalLoading] = useState<boolean>(false);
    const [isFiltersLoading, setIsFiltersLoading] = useState<boolean>(false);
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: ""
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })
    const [totalFilters, setTotalFilters] = useState<number>(0);
    const [tableData, setTableData] = useState<OrderListObj[]>([]);

    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false)
    const debounceSearchText = useDebounce(searchText, 500);



    const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map((obj: ProductTypeFilter) => `${obj.orderStatusId}`)

    const approvalFilterPayload = approvalFilterData?.filter(obj => obj.checked)?.map((obj: ApprovalStatuFilter) => `${obj.orderStatusId}`)




    const fetchOrderApprovalList = async ({
        sort = "",
        order = "",
        productType = [],
        approvalStatus = [],
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
    }: any) => {
        try {
            setOrderApprovalLoading(true);

            const payload = {
                productType,
                approvalStatus,
                searchInput,
                sort,
                order,
                currentPage,
                rowsPerPage
            }

            const orderApprovalListRes = await (dispatch(getOrderApprovalList(payload)) as unknown as Promise<OrderHistoryRes>)
            const { orders, totalCount } = orderApprovalListRes;

            setTableData(orders);
            setTablePaginationObj(prevState => ({
                ...prevState,
                totalRows: totalCount
            }))
        } catch (error) {
            console.log('error', error);
        } finally {
            setOrderApprovalLoading(false);
        }
    }

    const handleOpenFilterDrawer = () => {
        setFilterDrawerOpen(true)
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }

    const handleApplyFilters = () => {
        setFilterDrawerOpen(false)

        const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId)

        const approvalFilterPayload = approvalFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId);

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        const totalAppliedFilters =
            (productFilterPayload?.length ?? 0) + (approvalFilterPayload?.length ?? 0);

        setTotalFilters(totalAppliedFilters)


        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const isFilterApplied = productFilterData?.some(obj => obj.checked) || approvalFilterData?.some(obj => obj.checked)

    const handleFiltersClearAll = () => {
        setProductFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setApprovalFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        setTotalFilters(0)

        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            searchInput: debounceSearchText?.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage
        })

        setFilterDrawerOpen(false)
    }

    const handleProductTypeFilterChange = (value: string, checked: boolean) => {
        setProductFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleApprovalTypeFilterChange = (value: string, checked: boolean) => {
        setApprovalFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))
        e.preventDefault()
    }

    const handleSort = async (orderBy: string, order: Order) => {

        setTableSortingObj({
            order,
            orderBy
        })

        fetchOrderApprovalList({
            sort: orderBy,
            order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            searchInput: debounceSearchText?.trim(),
            currentPage: tablePaginationObj?.currentPage + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))
        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            searchInput: debounceSearchText.trim(),
            rowsPerPage: Number(event.target.value),
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    useEffect(() => {
        async function fetchData() {
            if (productFilterData?.length === 0 || approvalFilterData?.length === 0) {

                try {
                    setIsFiltersLoading(true);
                    const productFilterRes = await (dispatch(getProductTypeFilter()) as unknown as Promise<ProductTypeFilter[]>)
                    setProductFilterData(productFilterRes);

                    const approvalFilterRes = await (dispatch(getApprovalTypeFilter()) as unknown as Promise<ApprovalStatuFilter[]>)
                    setApprovalFilterData(approvalFilterRes?.map(obj => {
                        if (obj?.orderStatusId?.toLowerCase() === "pending") {
                            return { ...obj, checked: true }
                        }
                        return obj
                    }));

                    await fetchOrderApprovalList({
                        sort: tableSortingObj?.orderBy,
                        order: tableSortingObj?.order,
                        productType: productFilterRes
                            ?.filter(obj => obj.checked)
                            ?.map(obj => obj?.orderStatusId),
                        approvalStatus: approvalFilterRes
                            ?.map(obj => {
                                if (obj?.orderStatusId?.toLowerCase() === "pending") {
                                    return { ...obj, checked: true }
                                }
                                return obj
                            })
                            ?.filter(obj => obj?.checked)
                            ?.map(obj => obj?.orderStatusId),
                        searchInput: debounceSearchText.trim(),
                        currentPage: 1,
                        rowsPerPage: tablePaginationObj?.rowsPerPage,
                    })

                    setTotalFilters(1);
                } catch (e) {
                    console.log('error', e);
                }
                finally {
                    setIsFiltersLoading(false);
                }
                return
            }

            await fetchOrderApprovalList({
                sort: tableSortingObj?.orderBy,
                order: tableSortingObj?.order,
                productType: productFilterPayload,
                approvalStatus: approvalFilterPayload,
                searchInput: debounceSearchText.trim(),
                currentPage: 1,
                rowsPerPage: tablePaginationObj?.rowsPerPage,
            })
        }

        fetchData()
    }, [debounceSearchText])

    return (
        // <CommonLayoutForPOA>
        <CommonLayout>
            <>
                <Box>
                    <Typography sx={{ color: 'text.primary', mb: 2.5 }} variant='h1'>
                        Order Approvals
                    </Typography>

                    <Box sx={{ background: palette.background.paper, borderRadius: '25px', overflow: 'hidden', padding: '30px 25px' }}>
                        <Box sx={{ mb: '35px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                            <FormControl variant="standard" sx={{ display: 'block' }}>
                                <TextField
                                    id="outlined-multiline-flexible"
                                    value={searchText}
                                    placeholder={`Search  Order`}

                                    onChange={handleSearchTextChange}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                                    onMouseDown={handleClearSearch}
                                                >
                                                    <DialogCloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        style: {
                                            borderRadius: '5px',
                                            height: '40px',
                                            width: '100%',
                                            maxWidth: '255px',
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            </FormControl>
                            <Button
                                variant="text"
                                startIcon={
                                    <Badge
                                        showZero
                                        badgeContent={totalFilters}
                                        sx={(theme: Theme) => ({
                                            '& .MuiBadge-badge': {
                                                right: 0,
                                                top: -3,
                                                backgroundColor: theme.palette.primary[500],
                                                color: theme.palette.common.white,
                                                ...theme.typography.subtitle_600
                                            },
                                        })}
                                    >
                                        <FilterIcon style={{ opacity: 1 }} />
                                    </Badge>
                                }
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    padding: "5px",
                                    ...theme.typography.body_500,
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[50]
                                    }
                                })}
                                TouchRippleProps={{
                                    style: {
                                        color: palette.grey[300]
                                    }
                                }}
                                onClick={handleOpenFilterDrawer}
                                disabled={false}
                            >
                                Filters
                            </Button>
                        </Box>
                        <CustomTable
                            isLoading={orderApprovalLoading || isFiltersLoading}
                            tableBody={
                                <OrdersApprovalTableBody data={tableData ?? []} />
                            }
                            tableData={tableData ?? []}
                            tableHeaderData={tableHeaderData}
                            isPaginationVisible={true}
                            tableDataCount={tablePaginationObj?.totalRows ?? 0}
                            handleSort={handleSort}
                            order={tableSortingObj.order}
                            orderBy={tableSortingObj.orderBy}
                            handleRowsPerPageChange={handleRowsPerPageChange}
                            handlePageChange={handlePageChange}
                            currentPage={tablePaginationObj.currentPage}
                            rowsPerPageOptions={[5, 10, 20, 30]}
                            rowsPerPage={tablePaginationObj.rowsPerPage}
                            tableBodyLoaderRowCustomStyles={{
                                '& td:first-of-type': {
                                    paddingLeft: "0px"
                                },
                            }}
                            tableHeadRowCustomStyles={{
                                '& th:first-of-type': {
                                    paddingLeft: "0px"
                                }
                            }}
                            tableBodyCustomStyles={{
                                '& tr:last-child td': {
                                    border: 0
                                }
                            }}
                            loadingRowsNum={5}
                        />
                    </Box>
                </Box>
                <Drawer
                    open={isFilterDrawerOpen}
                    onClose={handleCloseFilterDrawer}
                    anchor="right"
                    PaperProps={{
                        sx: {
                            padding: "25px",
                            width: "450px"
                        }
                    }}
                    ModalProps={{
                        sx: (theme: Theme) => ({
                            backgroundColor: `${theme.palette.common.black}03`
                        })
                    }}
                >
                    <CustomFilter
                        isLoading={isFiltersLoading}
                        handleClearAll={handleFiltersClearAll}
                        handleApplyFilters={handleApplyFilters}
                        showCloseIcon={true}
                        handleCloseIconClick={handleCloseFilterDrawer}
                        showAmcFilter={false}
                        showCategoryFilter={false}
                        showRiskFilter={false}
                        showProductTypeFilter={true}
                        showPaymentTypeFilter={false}
                        showApprovalTypeFilter={true}
                        showTransactionTypeFilter={false}
                        productTypeFilterData={productFilterData}
                        approvalTypeFilterData={approvalFilterData}
                        handleProductTypeFilterChange={handleProductTypeFilterChange}
                        handleApprovalTypeFilterChange={handleApprovalTypeFilterChange}
                        isFilterApplied={isFilterApplied}
                        defaultExpandedFilter="productType"
                    />
                </Drawer>
            </>
            {/* </CommonLayoutForPOA> */}
        </CommonLayout>
    )
}
